import { useState, useEffect } from "react";

import { withTranslation } from "react-i18next";
import { Select, Box, FormControl, InputAdornment, MenuItem, TextField, Typography, Checkbox } from "@mui/material";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import Snackbar from "@plannel/components/Snackbar"
import Dialog from "@plannel/components/Dialog";
import { isEmpty, isEqual } from "lodash";
import p13nService from "@plannel/services/system/p13n-service";
import { FS } from "@plannel/utils/font-util";

const InvCovDaysSettings = (props) => {
  const { t, open, setOpen, applyInvCovDays, viewName, title } = props;

  const modalTitle = isEmpty(title) ? t("inventoryCoverageDaysSettings") : title;
  // 열 제목 규격(Chart Settings 와 동일)
  const headerSx = { fontSize: FS.BODY, color: "text.secondary" };
  const { snackBarMessageColor } = ColorUtil;
  const { inventoryCoverageDaysPolicy } = CodeUtil;
  const { INV_COV_DAYS_THRESHOLD, INV_COV_DAYS_APPLY, INV_COV_DAYS_COMPUTED_METHOD } = inventoryCoverageDaysPolicy;
  const [planConfig, setPlanConfig] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [activeYn, setActiveYn] = useState(false);
  const [days, setDays] = useState(0);
  const actions = ["lessThan", "greaterThan"];
  const [computedMethod, setComputedMethod] = useState("lessThan");

  const [initalConfigs, setInitalConfigs] = useState({});

  const initalizeConfigs = () => {
    setDays(initalConfigs[INV_COV_DAYS_THRESHOLD] ?? 0);
    setActiveYn(initalConfigs[INV_COV_DAYS_APPLY] ?? false);
    setComputedMethod(initalConfigs[INV_COV_DAYS_COMPUTED_METHOD] ?? "lessThan");
  };

  const retrievePlanConfig = () => {
    const configObj = {};

    p13nService.get(viewName)
      .then((response) => {
        if (response?.data) {
          const invCovDaysConfig = response.data;
          setPlanConfig(invCovDaysConfig);
          configObj[INV_COV_DAYS_THRESHOLD] = invCovDaysConfig[INV_COV_DAYS_THRESHOLD] ?? 0;
          configObj[INV_COV_DAYS_APPLY] = invCovDaysConfig[INV_COV_DAYS_APPLY] ?? false;
          configObj[INV_COV_DAYS_COMPUTED_METHOD] = invCovDaysConfig[INV_COV_DAYS_COMPUTED_METHOD] ?? "lessThan";

          setDays(invCovDaysConfig[INV_COV_DAYS_THRESHOLD] ?? 0);
          setActiveYn(invCovDaysConfig[INV_COV_DAYS_APPLY] ?? false);
          setComputedMethod(invCovDaysConfig[INV_COV_DAYS_COMPUTED_METHOD] ?? "lessThan");
          setInitalConfigs(configObj);

          applyInvCovDays(configObj);
        }
      }).catch(e => {
        debug(e);
      })
  };

  const handleChange = (event) => {
    const { target: { value } } = event;
    setComputedMethod(value)
  }

  const checkValidation = numVal => {
    return isNaN(numVal) || isEqual(numVal, "") || !Number.isInteger(Number(numVal));
  }

  const handleSave = () => {
    if (checkValidation(days)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntValue") });
      return false;
    }

    const params = [];

    const configObj = {...planConfig};
    configObj[INV_COV_DAYS_THRESHOLD] = days;
    configObj[INV_COV_DAYS_APPLY] = activeYn;
    configObj[INV_COV_DAYS_COMPUTED_METHOD] = computedMethod;

    params.push(configObj);

    const saveData = { pageCd: viewName, configObj };
    p13nService.upsert(saveData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        setOpen(false);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        retrievePlanConfig();
      });
    return true;
  };

  const handleClose = (action) => {
    if (isEqual(action, "cancel")) {
      setOpen(false);
      initalizeConfigs();
    } else if (isEqual(action, "save")) {
      handleSave();
    }
  };


  const handleDialog = (action) => {
    if (action === "SAVE") {
      handleSave();
    }
  };

  useEffect(() => {
    retrievePlanConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputPropsByValue = value => {
    return value ? { endAdornment: <InputAdornment position="end" sx={{ mr: 1 }}>{t("dayUnit")}</InputAdornment> } : {};
  }

  const handleCheckbox = e => {
    const value = e.target.checked;
    setActiveYn(value);
  }

  return (
    <>
      <BaseConfigModal
        open={open}
        onClose={() => handleClose("cancel")}
        title={modalTitle}
        onSave={() => handleClose("save")}
        /*
         * 본문은 md(360)에도 들어가지만 **제목**이 접힌다 — "Inventory Coverage Days Setting"(en)·
         * "Cài đặt số ngày đáp ứng tồn kho"(vi) 이 20px/700 로 약 340px, 좌우 패딩까지 380px 필요.
         */
        size="lg">
        {/* 열 제목을 입력 위에 둔다 — 다른 설정 모달과 같은 규격(MUI 부동 label 은 제목과 겹쳐 뺐다) */}
        <Box sx={{ display: "grid", gridTemplateColumns: "auto 120px 160px", gap: 2, alignItems: "center" }}>
          <Typography sx={headerSx}>{t("apply")}</Typography>
          <Typography sx={headerSx}>{t("days")}</Typography>
          <Typography sx={headerSx}>{t("computeMethod")}</Typography>

          <Checkbox
            size="small"
            color="primary"
            name={"apply"}
            checked={activeYn}
            onChange={handleCheckbox}
            sx={{ p: 0.25, justifySelf: "start" }}
          />
          {/* py 를 덮어쓰지 않는다 — 옆 Select 는 size="small" 기본 패딩(8.5px)을 쓰므로 높이가 어긋난다 */}
          <TextField
            size="small"
            name="days"
            value={days}
            required
            error={checkValidation(days)}
            InputProps={inputPropsByValue(days)}
            onChange={e => setDays(e.target.value)}
            sx={{ "& .MuiInputBase-input": { fontSize: FS.TITLE } }}
          />
          <FormControl size="small">
            <Select
              id="computedMethod"
              name="computedMethod"
              value={computedMethod}
              onChange={handleChange}
              sx={{ fontSize: FS.TITLE }}
            >
              {actions?.map((act) => (
                <MenuItem key={act} value={act} sx={{ fontSize: FS.TITLE }}>
                  {t(act)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </BaseConfigModal>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </>
  )
};

export default withTranslation()(InvCovDaysSettings);
